import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Hr from '__core/modules/atoms/Hr';
import Icon from '__core/modules/atoms/Icons';
import HeaderTabs from '__core/modules/components/tabs/HeaderTabs';
import FooterButtons from '__core/modules/compositions/footer/FooterButtons';
import Image from 'app/components/Image/Image';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React from 'react';
import { withTranslation } from 'react-i18next';

const DialogDefault = ({
  t,
  buttonTextCancel,
  buttonTextSucess,
  buttonTextSucessColor,
  children,
  className,
  customFooterLeft,
  customFooterRight,
  customHeader,
  disabledCancel = false,
  disabledClose,
  disabledOnBack = false,
  disabledSuccess = false,
  footerGray,
  footerStuffed = false,
  footerWithBackground,
  hasCustomFooter,
  headerGray,
  headline,
  hideHeader = false,
  htmlColor = '#fff',
  iconTitle,
  isKeyEnterSubmmit,
  loadingCancel,
  onBack,
  onBackFooter,
  onCancel,
  onChangeTab,
  onClose,
  onConfigClick,
  onGoBack,
  onSuccess,
  open = false,
  separator,
  showHeader = true,
  size = 'default',
  subtitle,
  tabActive,
  tabs,
  text,
  title,
  titleRigth,
  openSettingsMenu,
  shouldShowSettingsIcon,
  ...propsDialog
}) => {
  // Apply default values inside component to avoid "Cannot access before initialization" error
  const finalButtonTextCancel = buttonTextCancel || (t ? t('actions:Cancel') : 'Cancel');
  const finalButtonTextSucess = buttonTextSucess || (t ? t('actions:Confirm') : 'Confirm');

  const { tReady, ...props } = propsDialog;
  const handleKeyDown = (event) => {
    event.stopPropagation();

    if (isKeyEnterSubmmit && event.key === 'Enter' && !disabledSuccess) {
      event.preventDefault();
      onSuccess();
      return;
    }
    if (event.key === 'Escape' && onClose && !disabledClose) {
      event.preventDefault();
      onClose(event, 'escapeKeyDown');
    }
  };

  return (
    <div className={clsx('dialog')} onClick={(event) => event.stopPropagation()}>
      <Dialog
        aria-describedby="alert-dialog-description"
        aria-labelledby="alert-dialog-title"
        disableEnforceFocus={true}
        fullWidth={true}
        maxWidth={false}
        onClose={onClose}
        onKeyDown={handleKeyDown}
        open={open}
        className={clsx('dialog', className, `dialog--size-${size}`, {
          'dialog--with-footer-with-background': footerWithBackground,
          'dialog--with-icon': iconTitle,
          'dialog--footer-stuffed': footerStuffed,
        })}
        {...props}
      >
        {!hideHeader && (title || onClose || customHeader) && showHeader && (
          <div className={clsx('dialog__header', { 'dialog__header--background-gray': headerGray })}>
            {onBack && (
              <button type="button" className="hover-basic" onClick={onBack} disabled={disabledOnBack}>
                <Image src="/images/svg/back.svg" />
              </button>
            )}

            {onGoBack && (
              <button type="button" className="hover-basic" onClick={onGoBack}>
                <Icon type="icon-arrow-left" htmlColor={htmlColor} />
              </button>
            )}

            <div className="dialog__title-container">
              {title && (
                <div className="d-flex align-items-center justify-content-between w-100">
                  <div className="w-100">
                    {headline && <div className="dialog__headline">{headline}</div>}

                    <div className="dialog__title-wrapper">
                      {iconTitle && <Icon type={iconTitle} color="primary" />}
                      <span className="dialog__title">{title}</span>
                    </div>

                    {subtitle && <div className="dialog__subtitle">{subtitle}</div>}
                  </div>

                  {titleRigth && <div className="mr-3 w-100">{titleRigth}</div>}
                </div>
              )}

              {tabs && (
                <div className="dialog__title-tabs">
                  <HeaderTabs tabs={tabs} tabActive={tabActive} onChangeTab={onChangeTab} />
                </div>
              )}

              {!customHeader && (
                <div className="d-flex align-items-center dialog__header-icons">
                  {shouldShowSettingsIcon && (
                    <button type="button" className="dialog__general-settings" onClick={openSettingsMenu}>
                      <Icon
                        type="icon-general-settings"
                        size="big"
                        tooltip={t('generalSettings:general_settings')}
                        tooltipPlacement="left"
                        tooltipClasses={{
                          tooltip: 'dialog-settings-tooltip',
                          arrow: 'dialog-settings-arrow',
                        }}
                      />
                    </button>
                  )}
                  {onClose && (
                    <button type="button" className={disabledClose ? 'dialog__close-disabled' : 'dialog__close'} onClick={onClose}>
                      <Icon size="big" type="icon-close" />
                    </button>
                  )}
                </div>
              )}
            </div>
            {text && <span className="dialog__text">{text}</span>}
            {customHeader && <div className="dialog__custom-header">{customHeader}</div>}
            {separator && (
              <div className="dialog__hr">
                <Hr size="small" />
              </div>
            )}
          </div>
        )}

        {children && <DialogContent>{children}</DialogContent>}

        {(onSuccess || onCancel) && (
          <DialogActions className={clsx({ 'dialog__actions--background-gray': footerGray })}>
            <div className="dialog__actions">
              <div className="dialog__actions-left">{!!customFooterLeft && customFooterLeft}</div>
              <FooterButtons
                buttonTextCancel={finalButtonTextCancel}
                buttonTextSucess={finalButtonTextSucess}
                buttonTextSucessColor={buttonTextSucessColor}
                disabledCancel={disabledCancel}
                disabledSuccess={disabledSuccess}
                isCustomFooterLeft={!customFooterLeft}
                loadingCancel={loadingCancel}
                onBackFooter={onBackFooter}
                onCancel={onCancel}
                onSuccess={onSuccess}
                t={t}
              />
            </div>
          </DialogActions>
        )}

        {hasCustomFooter && (
          <DialogActions className={clsx({ 'dialog__actions--background-gray': footerGray })}>
            <div className="dialog__actions">
              <div className="dialog__actions-left">{!!customFooterLeft && customFooterLeft}</div>
              <div className="dialog__actions-left">{!!customFooterRight && customFooterRight}</div>
            </div>
          </DialogActions>
        )}
      </Dialog>
    </div>
  );
};

DialogDefault.propTypes = {
  buttonTextCancel: PropTypes.string,
  buttonTextSucess: PropTypes.string,
  buttonTextSucessColor: PropTypes.string,
  children: PropTypes.any,
  className: PropTypes.any,
  customFooterLeft: PropTypes.any,
  customFooterRight: PropTypes.any,
  customHeader: PropTypes.any,
  disabledCancel: PropTypes.bool,
  disabledClose: PropTypes.bool,
  disabledOnBack: PropTypes.bool,
  disabledSuccess: PropTypes.bool,
  footerGray: PropTypes.bool,
  footerStuffed: PropTypes.bool,
  footerWithBackground: PropTypes.any,
  hasCustomFooter: PropTypes.bool,
  headerGray: PropTypes.bool,
  headline: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  hideHeader: PropTypes.bool,
  htmlColor: PropTypes.string,
  iconTitle: PropTypes.any,
  isKeyEnterSubmmit: PropTypes.bool,
  loadingCancel: PropTypes.bool,
  onBack: PropTypes.func,
  onBackFooter: PropTypes.func,
  onCancel: PropTypes.func,
  onChangeTab: PropTypes.func,
  onClose: PropTypes.func,
  onConfigClick: PropTypes.func,
  onGoBack: PropTypes.func,
  onSuccess: PropTypes.func,
  open: PropTypes.bool,
  separator: PropTypes.any,
  showHeader: PropTypes.bool,
  size: PropTypes.oneOf(['default', 'xxs', 'xs', 's', 'small', 'xl', 'xxl', 'all-screen', 'custom-905']),
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  t: PropTypes.any,
  tabActive: PropTypes.any,
  tabs: PropTypes.any,
  text: PropTypes.any,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  titleRigth: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  openSettingsMenu: PropTypes.func,
  shouldShowSettingsIcon: PropTypes.bool,
};

export default withTranslation(['common', 'actions', 'placeholder', 'types-resources'])(DialogDefault);
